import { useEffect, useState } from 'react';

/**
 * This hook accepts a querySelector query and tries to retrieve the associated
 * element. If the element isn't found, it will retry based on a provided
 * interval (defaults to 500 milliseconds) until it finds the element.
 *
 * When the element is found, it is stored in state which will trigger a
 * re-render by React, so you can include the element in a dependency list (e.g.
 * `useEffect`s) to take action when the element is found.
 *
 * The primary use case for this hook is to bind event listeners to elements
 * that might not exist when the code is first run.
 *
 * @param query Query Selector query.
 * @param retryInterval How often the code should check for the element.
 */
function useElement(query: string, retryInterval: number = 500) {
  const [element, setElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!query) return;

    function getAndStoreElement(): boolean {
      const el = document.querySelector<HTMLElement>(query);

      setElement(el);

      return Boolean(el);
    }

    if (getAndStoreElement() || !retryInterval) {
      return () => {};
    }

    const t = window.setInterval(() => {
      if (getAndStoreElement()) {
        window.clearInterval(t);
      }
    }, retryInterval);

    return () => {
      window.clearInterval(t);
    };
  }, [query, retryInterval]);

  return element;
}

export default useElement;
